import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Upload, X, Image as ImageIcon, 
  Loader2, Trash2, Star, Package, Plus, Palette, Gem, Scale, MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import axios from 'axios';
import { compressImage } from '@/lib/imageCompression';
import { useAccessoriGroups } from '@/hooks/useAccessoriGroups';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const CATEGORIES = ['Anelli', 'Collane', 'Bracciali', 'Orecchini', 'Pendenti', 'Orologi'];
const ARGENTO_SUBS = ['Collane', 'Bracciali', 'Anelli', 'Orecchini'];
const MATERIALS = ['Oro Giallo', 'Oro Bianco', 'Oro Rosa', 'Argento', 'Argento - Giallo', 'Argento - Silver', 'Argento - Rosa', 'Platino', 'Acciaio', 'Policarbonato'];
const BRANDS = ['Rolex', 'Cartier', 'Bulgari', 'Tudor', 'D1 Milano'];
const GEMSTONES = [
  'Diamante', 'Smeraldo', 'Rubino', 'Zaffiro', 'Perla',
  'Topazio', 'Ametista', 'Acquamarina', 'Zircone', 'Granato',
];
const CLARITIES = ['FL', 'IF', 'VVS1', 'VVS2', 'VS', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2', 'I3'];
const COLORS = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
const CUTS = ['Eccellente', 'Molto Buono', 'Buono', 'Discreto', 'Scarso', 'Baguette', 'Princess', 'Goccia', 'Ovale', 'Smeraldo', 'Tondo'];

const MATERIAL_COLORS = {
  'Oro Giallo': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Oro Bianco': 'bg-gray-100 text-gray-800 border-gray-300',
  'Oro Rosa': 'bg-pink-100 text-pink-800 border-pink-300',
  'Argento': 'bg-slate-100 text-slate-800 border-slate-300',
  'Argento - Giallo': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Argento - Silver': 'bg-slate-100 text-slate-800 border-slate-300',
  'Argento - Rosa': 'bg-pink-100 text-pink-800 border-pink-300',
  'Platino': 'bg-zinc-100 text-zinc-800 border-zinc-300',
};

export default function ProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isNew = id === 'nuovo';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    collection: 'Generale',
    category: 'Anelli',
    material: 'Oro Giallo',
    brand: '',
    model_code: '',
    image_url: '',
    images: [],
    variants: [],
    gemstones: [],
    weight_grams: '',
    in_stock: true,
    featured: false,
    price_on_request: false
  });
  const accessoriGroups = useAccessoriGroups();
  const [newGroup, setNewGroup] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchProduct();
    }
  }, [id, isNew]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${API}/products/${id}`);
      const product = res.data;
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price ? product.price.toString() : '',
        collection: product.collection || 'Generale',
        price_on_request: !!product.price_on_request,
        category: product.category || 'Anelli',
        material: product.material || 'Oro Giallo',
        brand: product.brand || '',
        model_code: product.model_code || '',
        image_url: product.image_url || '',
        images: product.images || [],
        variants: product.variants || [],
        gemstones: product.gemstones || [],
        weight_grams: product.weight_grams != null ? String(product.weight_grams) : '',
        in_stock: product.in_stock ?? true,
        featured: product.featured ?? false
      });
    } catch (e) {
      toast.error('Prodotto non trovato');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Collezione: passando ad Argento il sottogruppo deve essere Collane/Bracciali
  const handleCollectionChange = (val) => {
    setFormData(prev => {
      let category = prev.category;
      if (val === 'Argento' && !ARGENTO_SUBS.includes(category)) category = 'Collane';
      if (val === 'Accessori' && !accessoriGroups.includes(category)) category = accessoriGroups[0] || 'Portachiavi';
      if (val === 'Generale' && !CATEGORIES.includes(category)) category = 'Anelli';
      setNewGroup(false);
      return { ...prev, collection: val, category };
    });
  };

  // === VARIANTI ===
  const handleAddVariant = () => {
    const usedMaterials = [formData.material, ...formData.variants.map(v => v.material)];
    const availableMaterial = MATERIALS.find(m => !usedMaterials.includes(m)) || MATERIALS[0];
    
    const newVariant = {
      id: `var_${Date.now()}`,
      material: availableMaterial,
      price: '',
      image_url: '',
      in_stock: true,
      sku: ''
    };
    handleChange('variants', [...formData.variants, newVariant]);
  };

  const handleUpdateVariant = (variantId, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map(v => (v.id === variantId ? { ...v, [field]: value } : v)),
    }));
  };

  const handleRemoveVariant = (variantId) => {
    setFormData(prev => ({ ...prev, variants: prev.variants.filter(v => v.id !== variantId) }));
  };

  // === GEMME / PIETRE ===
  const handleAddGemstone = () => {
    handleChange('gemstones', [...formData.gemstones, { type: GEMSTONES[0], carats: '', clarity: '', color: '', cut: '' }]);
  };

  const handleUpdateGemstone = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      gemstones: prev.gemstones.map((g, i) => (i === index ? { ...g, [field]: value } : g)),
    }));
  };

  const handleRemoveGemstone = (index) => {
    setFormData(prev => ({ ...prev, gemstones: prev.gemstones.filter((_, i) => i !== index) }));
  };

  // === IMMAGINI ===
  const handleImageUpload = async (e, variantId = null) => {
    const original = e.target.files?.[0];
    if (!original) return;

    // Limite di sicurezza assoluto prima della compressione
    if (original.size > 40 * 1024 * 1024) {
      toast.error('Immagine troppo grande. Max 40MB.');
      return;
    }

    setUploading(true);
    try {
      // Compressione automatica se l'immagine supera ~4MB
      let file = original;
      if (original.size > 4 * 1024 * 1024) {
        toast.info('Comprimo l\'immagine...');
        file = await compressImage(original);
      }
      // Se anche dopo la compressione supera il limite del server (15MB)
      if (file.size > 15 * 1024 * 1024) {
        toast.error('Immagine troppo grande anche dopo la compressione.');
        return;
      }

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = res.data.url;

      if (variantId) {
        // Aggiornamento funzionale: non perde modifiche concorrenti
        setFormData(prev => ({
          ...prev,
          variants: prev.variants.map(v => (v.id === variantId ? { ...v, image_url: imageUrl } : v)),
        }));
      } else {
        // Aggiornamento funzionale: aggiungere più foto in sequenza non ne perde nessuna
        setFormData(prev => ({
          ...prev,
          image_url: prev.image_url || imageUrl,
          images: [...prev.images, imageUrl],
        }));
      }
      toast.success('Immagine caricata');
    } catch (e) {
      toast.error('Errore caricamento immagine');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = (imageUrl) => {
    setFormData(prev => {
      const newImages = prev.images.filter(img => img !== imageUrl);
      return {
        ...prev,
        images: newImages,
        image_url: prev.image_url === imageUrl ? (newImages[0] || '') : prev.image_url,
      };
    });
  };

  const handleSetMainImage = (imageUrl) => {
    handleChange('image_url', imageUrl);
    toast.success('Immagine principale aggiornata');
  };

  // Riordino immagini (drag & drop) — l'ordine determina la galleria sul sito
  const handleReorderImages = (from, to) => {
    if (from == null || from === to) return;
    setFormData(prev => {
      const arr = [...prev.images];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...prev, images: arr };
    });
  };

  const handleAddImageUrl = () => {
    const input = document.getElementById('image-url-input');
    const url = input.value.trim();
    if (url) {
      setFormData(prev => ({
        ...prev,
        image_url: prev.image_url || url,
        images: [...prev.images, url],
      }));
      input.value = '';
      toast.success('Immagine aggiunta');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Inserisci il nome del prodotto');
      return;
    }
    if (!formData.category.trim()) {
      toast.error('Inserisci il sottogruppo');
      return;
    }
    const noPrice = !formData.price || parseFloat(formData.price) <= 0;
    const priceOnRequest = formData.price_on_request || noPrice;
    if (!formData.image_url) {
      toast.error('Carica almeno un\'immagine');
      return;
    }

    setSaving(true);
    
    const variants = formData.variants.map(v => ({
      ...v,
      price: v.price ? parseFloat(v.price) : null,
      image_url: v.image_url || null
    }));

    const gemstones = formData.gemstones
      .filter((g) => g.type)
      .map((g) => ({
        type: g.type,
        carats: g.carats !== '' && g.carats != null ? parseFloat(g.carats) : null,
        clarity: g.clarity || null,
        color: g.color || null,
        cut: g.cut || null,
      }));

    const payload = {
      ...formData,
      category: formData.category.trim(),
      price: noPrice ? 0 : parseFloat(formData.price),
      price_on_request: priceOnRequest,
      weight_grams: formData.weight_grams !== '' ? parseFloat(formData.weight_grams) : null,
      gemstones,
      variants
    };

    try {
      if (isNew) {
        await axios.post(`${API}/products`, payload);
        toast.success('Prodotto creato con successo');
      } else {
        await axios.put(`${API}/products/${id}`, payload);
        toast.success('Prodotto aggiornato con successo');
      }
      navigate('/admin');
    } catch (e) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#722F37]" />
      </div>
    );
  }

  const usedMaterials = [formData.material, ...formData.variants.map(v => v.material)];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="product-edit-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="font-medium text-gray-900 text-base md:text-lg">
                  {isNew ? 'Nuovo Prodotto' : 'Modifica Prodotto'}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline"
                onClick={() => navigate('/admin')}
                className="rounded-full"
              >
                Annulla
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={saving}
                className="bg-[#722F37] hover:bg-[#5A252C] rounded-full"
                data-testid="save-product-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Images */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-[#722F37]" />
                Immagini
              </h2>
              <span className="text-xs text-gray-400">{formData.images.length} immagini</span>
            </div>
            {formData.images.length > 1 && (
              <p className="text-xs text-gray-400 mb-3">
                Trascina le immagini per riordinare la galleria. La prima/principale appare per prima sul sito.
              </p>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
              {formData.images.map((img, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleReorderImages(dragIndex, index); setDragIndex(null); }}
                  onDragEnd={() => setDragIndex(null)}
                  className={`relative aspect-square rounded-xl overflow-hidden group border-2 transition-all cursor-grab active:cursor-grabbing ${
                    dragIndex === index ? 'opacity-40' : ''
                  } ${
                    formData.image_url === img ? 'border-[#722F37] ring-2 ring-[#722F37]/20' : 'border-transparent hover:border-gray-200'
                  }`}
                  data-testid={`edit-image-${index}`}
                >
                  <img src={img} alt={`Product ${index + 1}`} className="w-full h-full object-cover pointer-events-none" />
                  {/* Controlli sempre visibili (funzionano anche su tablet/touch) */}
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(img)}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 shadow text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"
                    data-testid={`remove-image-${index}`}
                    aria-label="Rimuovi immagine"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {formData.image_url !== img && (
                    <button
                      type="button"
                      onClick={() => handleSetMainImage(img)}
                      className="absolute bottom-2 left-2 px-2.5 h-7 rounded-full bg-white/95 shadow text-gray-700 text-[11px] flex items-center gap-1 hover:bg-[#722F37] hover:text-white transition-colors"
                      data-testid={`set-main-${index}`}
                      aria-label="Imposta come principale"
                    >
                      <Star className="w-3.5 h-3.5" /> Principale
                    </button>
                  )}
                  {formData.image_url === img && (
                    <Badge className="absolute top-2 left-2 bg-[#722F37] text-white text-[10px] px-2">Principale</Badge>
                  )}
                </div>
              ))}
              
              <label className="aspect-square rounded-xl border-2 border-dashed border-gray-200 hover:border-[#722F37] hover:bg-[#722F37]/5 transition-all cursor-pointer flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-[#722F37]">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e)} disabled={uploading} />
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Upload className="w-6 h-6" /><span className="text-xs font-medium">Carica</span></>}
              </label>
            </div>
            
            <div className="pt-4 border-t border-gray-100">
              <Label className="text-xs text-gray-500 mb-2 block">Oppure inserisci URL immagine</Label>
              <div className="flex gap-2">
                <Input id="image-url-input" placeholder="https://..." className="rounded-full bg-gray-50 border-0" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddImageUrl())} />
                <Button type="button" variant="outline" className="rounded-full" onClick={handleAddImageUrl}>Aggiungi</Button>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-[#722F37]" />
              Informazioni Prodotto
            </h2>
            
            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-sm text-gray-700">Nome Prodotto *</Label>
                <Input id="name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="es. Anello Vesuvio" className="mt-1.5 rounded-xl bg-gray-50 border-0 h-12" />
              </div>

              <div>
                <Label htmlFor="model_code" className="text-sm text-gray-700">Modello (codice)</Label>
                <Input id="model_code" value={formData.model_code} onChange={(e) => handleChange('model_code', e.target.value)} placeholder="es. CG-1952-AV" className="mt-1.5 rounded-xl bg-gray-50 border-0 h-12" data-testid="model-code-input" />
              </div>
              
              <div>
                <Label htmlFor="description" className="text-sm text-gray-700">Descrizione</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Descrivi il prodotto..." className="mt-1.5 rounded-xl bg-gray-50 border-0 min-h-[120px] resize-none" />
              </div>

              <div>
                <Label className="text-sm text-gray-700">Collezione *</Label>
                <Select value={formData.collection} onValueChange={handleCollectionChange}>
                  <SelectTrigger className="mt-1.5 rounded-xl bg-gray-50 border-0 h-12" data-testid="collection-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Generale">Generale (catalogo)</SelectItem>
                    <SelectItem value="Argento">Collezione Argento</SelectItem>
                    <SelectItem value="Accessori">Accessori</SelectItem>
                  </SelectContent>
                </Select>
                {formData.collection === 'Argento' && (
                  <p className="text-xs text-gray-400 mt-1.5">
                    Sottogruppo: scegli <strong>Collane</strong>, <strong>Bracciali</strong>, <strong>Anelli</strong> o <strong>Orecchini</strong> qui sotto. Il pezzo apparirà <strong>solo</strong> nella Collezione Argento, non nel catalogo generale.
                  </p>
                )}
                {formData.collection === 'Accessori' && (
                  <p className="text-xs text-gray-400 mt-1.5">
                    Scegli un sottogruppo (es. <strong>Portachiavi</strong>) o creane uno nuovo: comparirà da solo nella tendina "Accessori" del sito.
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price" className="text-sm text-gray-700">Prezzo Base (EUR)</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                    <Input id="price" type="number" step="0.01" min="0" value={formData.price} onChange={(e) => handleChange('price', e.target.value)} placeholder="Su richiesta" className="rounded-xl bg-gray-50 border-0 h-12 pl-8" data-testid="price-input" />
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Lascia vuoto per mostrare "Prezzo su richiesta"</p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-700">{formData.collection === 'Generale' ? 'Categoria *' : 'Sottogruppo *'}</Label>
                  {newGroup ? (
                    <div className="mt-1.5 flex gap-2">
                      <Input autoFocus value={formData.category} onChange={(e) => handleChange('category', e.target.value)} placeholder="es. Gemelli" className="rounded-xl bg-gray-50 border-0 h-12" data-testid="new-group-input" />
                      <Button type="button" variant="ghost" size="icon" className="h-12 w-12 flex-shrink-0" onClick={() => { setNewGroup(false); handleChange('category', accessoriGroups[0] || 'Portachiavi'); }} aria-label="Annulla nuovo sottogruppo" data-testid="new-group-cancel"><X className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                  <Select key={formData.collection} value={formData.category} onValueChange={(val) => { if (!val) return; if (val === '__new__') { setNewGroup(true); handleChange('category', ''); } else handleChange('category', val); }}>
                    <SelectTrigger className="mt-1.5 rounded-xl bg-gray-50 border-0 h-12" data-testid="category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(formData.collection === 'Argento' ? ARGENTO_SUBS : formData.collection === 'Accessori' ? Array.from(new Set([...accessoriGroups, formData.category].filter(Boolean))) : CATEGORIES).map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      {formData.collection === 'Accessori' && <SelectItem value="__new__">+ Nuovo sottogruppo…</SelectItem>}
                    </SelectContent>
                  </Select>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm text-gray-700">Materiale Predefinito *</Label>
                  <Select value={formData.material} onValueChange={(val) => handleChange('material', val)}>
                    <SelectTrigger className="mt-1.5 rounded-xl bg-gray-50 border-0 h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MATERIALS.map(mat => <SelectItem key={mat} value={mat}>{mat}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.category === 'Orologi' && (
                <div>
                  <Label className="text-sm text-gray-700">Brand</Label>
                  <Select value={formData.brand || '_none'} onValueChange={(val) => handleChange('brand', val === '_none' ? '' : val)}>
                    <SelectTrigger className="mt-1.5 rounded-xl bg-gray-50 border-0 h-12" data-testid="brand-select">
                      <SelectValue placeholder="Seleziona brand" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— Nessun brand —</SelectItem>
                      {BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* GEMME & SPECIFICHE */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium flex items-center gap-2">
                <Gem className="w-5 h-5 text-[#722F37]" />
                Gemme & Specifiche
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={handleAddGemstone} className="rounded-full" disabled={formData.gemstones.length >= GEMSTONES.length} data-testid="add-gemstone-btn">
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi Pietra
              </Button>
            </div>

            {/* Peso totale */}
            <div className="mb-5">
              <Label className="text-sm text-gray-700 flex items-center gap-2">
                <Scale className="w-4 h-4 text-gray-400" /> Peso Totale (grammi)
              </Label>
              <div className="relative mt-1.5 max-w-xs">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.weight_grams}
                  onChange={(e) => handleChange('weight_grams', e.target.value)}
                  placeholder="es. 4.50"
                  className="rounded-xl bg-gray-50 border-0 h-12 pr-10"
                  data-testid="weight-grams-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">g</span>
              </div>
            </div>

            {/* Pietre */}
            {formData.gemstones.length === 0 ? (
              <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl border border-gray-100">
                <Gem className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessuna pietra aggiunta</p>
                <p className="text-xs mt-1">Aggiungi diamanti, smeraldi o altre gemme con i relativi carati</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.gemstones.map((g, index) => {
                  return (
                    <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-100" data-testid={`gemstone-row-${index}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                          <div>
                            <Label className="text-xs text-gray-500">Pietra</Label>
                            <Select value={g.type} onValueChange={(val) => handleUpdateGemstone(index, 'type', val)}>
                              <SelectTrigger className="mt-1 rounded-lg h-10 bg-white" data-testid={`gemstone-type-${index}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {GEMSTONES.map((s) => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Carati</Label>
                            <div className="relative mt-1">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={g.carats ?? ''}
                                onChange={(e) => handleUpdateGemstone(index, 'carats', e.target.value)}
                                placeholder="es. 1.20"
                                className="rounded-lg h-10 bg-white pr-12"
                                data-testid={`gemstone-carats-${index}`}
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">ct</span>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Colore</Label>
                            <Select value={g.color || '_none'} onValueChange={(val) => handleUpdateGemstone(index, 'color', val === '_none' ? '' : val)}>
                              <SelectTrigger className="mt-1 rounded-lg h-10 bg-white" data-testid={`gemstone-color-${index}`}>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">— Non specificato —</SelectItem>
                                {COLORS.map((c) => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Purezza</Label>
                            <Select value={g.clarity || '_none'} onValueChange={(val) => handleUpdateGemstone(index, 'clarity', val === '_none' ? '' : val)}>
                              <SelectTrigger className="mt-1 rounded-lg h-10 bg-white" data-testid={`gemstone-clarity-${index}`}>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">— Non specificata —</SelectItem>
                                {CLARITIES.map((c) => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Taglio</Label>
                            <Select value={g.cut || '_none'} onValueChange={(val) => handleUpdateGemstone(index, 'cut', val === '_none' ? '' : val)}>
                              <SelectTrigger className="mt-1 rounded-lg h-10 bg-white" data-testid={`gemstone-cut-${index}`}>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">— Non specificato —</SelectItem>
                                {CUTS.map((c) => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveGemstone(index)} className="mt-6 rounded-full text-red-500 hover:text-red-600 hover:bg-red-50 flex-shrink-0" data-testid={`remove-gemstone-${index}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* VARIANTI */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium flex items-center gap-2">
                <Palette className="w-5 h-5 text-[#722F37]" />
                Varianti Materiale
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={handleAddVariant} className="rounded-full" disabled={usedMaterials.length >= MATERIALS.length} data-testid="add-variant-btn">
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi Variante
              </Button>
            </div>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <Badge className={`rounded-full px-3 py-1 ${MATERIAL_COLORS[formData.material] || 'bg-gray-100'}`}>{formData.material}</Badge>
                <span className="text-xs text-gray-500">Predefinito</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Prezzo: <strong>{formData.price ? `€${formData.price}` : '-'}</strong></span>
                <span>•</span>
                <span className={formData.in_stock ? 'text-green-600' : 'text-red-600'}>{formData.in_stock ? 'Disponibile' : 'Esaurito'}</span>
              </div>
            </div>

            {formData.variants.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Palette className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessuna variante aggiuntiva</p>
                <p className="text-xs mt-1">Aggiungi varianti per offrire lo stesso prodotto in diversi materiali</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.variants.map((variant, index) => (
                  <div key={variant.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        {variant.image_url ? (
                          <div className="w-16 h-16 rounded-lg overflow-hidden relative bg-gray-100">
                            <img
                              src={variant.image_url}
                              alt={variant.material}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.currentTarget.style.opacity = '0.25'; }}
                            />
                            <button
                              type="button"
                              onClick={() => handleUpdateVariant(variant.id, 'image_url', '')}
                              className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white shadow text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"
                              data-testid={`remove-variant-image-${variant.id}`}
                              aria-label="Rimuovi foto variante"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-200 hover:border-[#722F37] flex flex-col items-center justify-center cursor-pointer transition-colors text-gray-400">
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, variant.id)} />
                            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
                          </label>
                        )}
                      </div>

                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs text-gray-500">Materiale</Label>
                          <Select value={variant.material} onValueChange={(val) => handleUpdateVariant(variant.id, 'material', val)}>
                            <SelectTrigger className="mt-1 rounded-lg h-10 bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {MATERIALS.filter(m => m === variant.material || !usedMaterials.includes(m)).map(mat => <SelectItem key={mat} value={mat}>{mat}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label className="text-xs text-gray-500">Prezzo (vuoto = base)</Label>
                          <div className="relative mt-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                            <Input type="number" step="0.01" min="0" value={variant.price || ''} onChange={(e) => handleUpdateVariant(variant.id, 'price', e.target.value)} placeholder={formData.price || '0.00'} className="rounded-lg h-10 bg-white pl-7" />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-xs text-gray-500">SKU (opzionale)</Label>
                          <Input value={variant.sku || ''} onChange={(e) => handleUpdateVariant(variant.id, 'sku', e.target.value)} placeholder="ABC-123" className="mt-1 rounded-lg h-10 bg-white" />
                        </div>
                        
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label className="text-xs text-gray-500">Disponibile</Label>
                            <div className="mt-1 flex items-center h-10">
                              <Switch checked={variant.in_stock} onCheckedChange={(val) => handleUpdateVariant(variant.id, 'in_stock', val)} />
                              <span className={`ml-2 text-sm ${variant.in_stock ? 'text-green-600' : 'text-red-600'}`}>{variant.in_stock ? 'Sì' : 'No'}</span>
                            </div>
                          </div>
                          <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveVariant(variant.id)} className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-lg font-medium mb-4">Stato e Visibilità</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${formData.in_stock ? 'bg-green-100' : 'bg-red-100'}`}>
                    <Package className={`w-5 h-5 ${formData.in_stock ? 'text-green-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Disponibile</p>
                    <p className="text-sm text-gray-500">Il prodotto base è acquistabile</p>
                  </div>
                </div>
                <Switch checked={formData.in_stock} onCheckedChange={(val) => handleChange('in_stock', val)} />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${formData.featured ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                    <Star className={`w-5 h-5 ${formData.featured ? 'text-yellow-600 fill-yellow-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">In Evidenza</p>
                    <p className="text-sm text-gray-500">Mostra nella homepage</p>
                  </div>
                </div>
                <Switch checked={formData.featured} onCheckedChange={(val) => handleChange('featured', val)} />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${formData.price_on_request ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <MessageCircle className={`w-5 h-5 ${formData.price_on_request ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Prezzo su richiesta</p>
                    <p className="text-sm text-gray-500">Nasconde il prezzo: il cliente chiede su WhatsApp</p>
                  </div>
                </div>
                <Switch checked={formData.price_on_request} onCheckedChange={(val) => handleChange('price_on_request', val)} data-testid="price-on-request-switch" />
              </div>
            </div>
          </div>

          {/* Mobile Actions */}
          <div className="lg:hidden flex gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/admin')} className="flex-1 rounded-full h-12">Annulla</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-[#722F37] hover:bg-[#5A252C] rounded-full h-12">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
